import { getPageDef, getPageKeyByPath, MODULES } from "@repo/permissions";

import {
  filterSidebarNavByModule,
  searchSidebarPages,
  sidebarSubItemMatchesPath,
} from "./sidebar-nav.utils.js";
import { SIDEBAR_MODULE_OPTIONS } from "./sidebar-nav.config.js";

const GDPR_SHARED_PATH_PREFIXES = [
  "/definitions/kvkk",
  "/definitions/privacy",
  "/definitions/obtaining-explicit-consent",
  "/definitions/application-tracking",
  "/definitions/compliance",
];

function isDestinationUrl(url) {
  return typeof url === "string"
    && url.startsWith("/")
    && !url.startsWith("//")
    && url !== "/";
}

function normalizePathname(pathname) {
  if (typeof pathname !== "string" || pathname.length === 0) return "";
  const clean = pathname.split("?")[0].split("#")[0] || "/";
  if (clean === "/") return clean;
  return clean.replace(/\/$/, "");
}

function dedupePages(pages) {
  const seenUrls = new Set();
  return (pages || []).filter((page) => {
    if (!isDestinationUrl(page?.url) || seenUrls.has(page.url)) return false;
    seenUrls.add(page.url);
    return true;
  });
}

function walkNavigationItems(items, inheritedSection, visit) {
  for (const item of items || []) {
    const section = item.section || inheritedSection;
    visit(item, section);
    walkNavigationItems(item.items, section, visit);
  }
}

function collectQuickAccessUrls(allData) {
  const urls = new Set();
  walkNavigationItems(allData?.navMain, undefined, (item, section) => {
    if (section === "quickAccess" && isDestinationUrl(item.url)) urls.add(item.url);
  });
  walkNavigationItems(allData?.navSecondary, undefined, (item, section) => {
    if (section === "quickAccess" && isDestinationUrl(item.url)) urls.add(item.url);
  });
  return urls;
}

function pageMatchesPath(pathname, page) {
  const path = normalizePathname(pathname);
  if (!path || !page?.url) return false;
  return sidebarSubItemMatchesPath(path, {
    ...page,
    url: normalizePathname(page.url),
  });
}

function workspaceContainsPath(pathname, pages) {
  return (pages || []).some((page) => pageMatchesPath(pathname, page));
}

function isValidWorkspace(workspaceKey, moduleWorkspaces, customWorkspaces) {
  if (!workspaceKey || workspaceKey === "all") return workspaceKey === "all";
  return moduleWorkspaces.some(({ key }) => key === workspaceKey)
    || customWorkspaces.some(({ id }) => id === workspaceKey);
}

function getWorkspacePages(workspaceKey, moduleWorkspaces, customWorkspaces, allPages) {
  const module = moduleWorkspaces.find(({ key }) => key === workspaceKey);
  if (module) return module.pages;
  const custom = customWorkspaces.find(({ id }) => id === workspaceKey);
  if (!custom) return [];
  const selectedUrls = new Set(custom.pageUrls || []);
  return allPages.filter((page) => selectedUrls.has(page.url));
}

function resolveCanonicalModule(page, moduleWorkspaces) {
  const canonicalModule = getPageDef(getPageKeyByPath(page?.url))?.module;
  if (canonicalModule && moduleWorkspaces.some(({ key }) => key === canonicalModule)) {
    return canonicalModule;
  }
  return moduleWorkspaces.find(({ pages }) => pages.some(({ url }) => url === page?.url))?.key;
}

/**
 * Resolve the licensed module relationships represented by one sidebar node.
 * @param {Record<string, unknown>} item
 * @returns {string[]}
 */
export function resolveSidebarItemModules(item) {
  const pageKeys = [
    item?.pageKey || item?.permissionKey || getPageKeyByPath(item?.url),
    ...(item?.anyPageKeys || []),
  ].filter(Boolean);
  const modules = new Set();

  for (const pageKey of pageKeys) {
    const page = getPageDef(pageKey);
    const pageModules = page?.anyModule?.length ? page.anyModule : [page?.module];
    pageModules.filter(Boolean).forEach((moduleKey) => modules.add(moduleKey));
  }
  if (item?.url === "/definitions/integrations/tenant-directory") {
    modules.add(MODULES.DIRECTORY);
  }
  if (item?.url === "/definitions/scale/bia-impact-scale") {
    modules.add(MODULES.BCM);
  }
  if (GDPR_SHARED_PATH_PREFIXES.some(
    (prefix) => item?.url === prefix || item?.url?.startsWith(`${prefix}/`),
  )) {
    modules.add(MODULES.GDPR);
  }
  return [...modules];
}

/**
 * Build the authorized page catalog and module workspaces from one sidebar tree.
 * @param {{ allData?: object, licensedModules?: string[] }} options
 * @returns {{ allPages: object[], moduleWorkspaces: object[], quickAccessUrls: Set<string> }}
 */
export function buildModuleNavigation({ allData, licensedModules = [] } = {}) {
  const allPages = dedupePages(searchSidebarPages("", allData || { navMain: [] }));
  const quickAccessUrls = collectQuickAccessUrls(allData);
  const licensed = new Set([MODULES.CORE, ...(licensedModules || [])]);
  const moduleWorkspaces = [];

  for (const option of SIDEBAR_MODULE_OPTIONS) {
    if (!licensed.has(option.key)) continue;

    const data = filterSidebarNavByModule(allData || { navMain: [] }, option.key, resolveSidebarItemModules);
    const pages = dedupePages(searchSidebarPages("", data))
      .filter((page) => !quickAccessUrls.has(page.url));
    if (pages.length === 0) continue;

    const featuredIds = new Set(option.featuredIds || []);
    const featuredPages = [];
    const featuredPageIds = new Set();
    for (const id of featuredIds) {
      const page = pages.find((candidate) => candidate.id === id);
      if (!page || featuredPageIds.has(page.url)) continue;
      featuredPages.push(page);
      featuredPageIds.add(page.url);
    }
    for (const page of pages) {
      if (featuredPageIds.has(page.url) || featuredIds.has(page.id)) continue;
      if (featuredPages.length >= 6) break;
      featuredPages.push(page);
      featuredPageIds.add(page.url);
    }

    moduleWorkspaces.push({
      key: option.key,
      defaultLabel: option.defaultLabel,
      icon: option.icon,
      descriptionKey: `home:moduleDescriptions.${option.key}`,
      data,
      pages,
      featuredPages,
    });
  }

  return { allPages, moduleWorkspaces, quickAccessUrls };
}

/**
 * Keep workspace selection stable where possible, then choose the canonical
 * authorized module for a deep link.
 * @param {{ pathname?: string, requestedWorkspace?: string, moduleWorkspaces?: object[], customWorkspaces?: object[], allPages?: object[], quickAccessUrls?: Set<string> }} options
 * @returns {string}
 */
export function resolveWorkspaceForPath({
  pathname,
  requestedWorkspace = "all",
  moduleWorkspaces = [],
  customWorkspaces = [],
  allPages = [],
  quickAccessUrls = new Set(),
} = {}) {
  const currentWorkspace = isValidWorkspace(requestedWorkspace, moduleWorkspaces, customWorkspaces)
    ? requestedWorkspace
    : "all";
  if (currentWorkspace === "all") return "all";
  const currentPages = getWorkspacePages(currentWorkspace, moduleWorkspaces, customWorkspaces, allPages);
  const currentIncludesPath = workspaceContainsPath(pathname, currentPages)
    || allPages.some((page) => quickAccessUrls.has(page.url) && pageMatchesPath(pathname, page));

  if (currentIncludesPath) return currentWorkspace;

  const matchingPages = allPages
    .map((page, index) => ({ page, index, path: normalizePathname(page.url) }))
    .filter(({ page }) => pageMatchesPath(pathname, page))
    .sort((left, right) => {
      const leftExact = normalizePathname(pathname) === left.path ? 1 : 0;
      const rightExact = normalizePathname(pathname) === right.path ? 1 : 0;
      if (leftExact !== rightExact) return rightExact - leftExact;
      if (left.path.length !== right.path.length) return right.path.length - left.path.length;
      return left.index - right.index;
    });

  const matchedPage = matchingPages[0]?.page;
  if (!matchedPage) return currentWorkspace;

  return resolveCanonicalModule(matchedPage, moduleWorkspaces) || currentWorkspace;
}
